import { z } from 'zod';

/**
 * CUSTOMISABLE COLUMNS — which fields a person wants to see, remembered.
 *
 * The lists in this product show what somebody decided, once, was worth seeing. That decision
 * was right for most shops and wrong for some: a boutique that never records a tag wants that
 * column's space back, and a wholesaler wants the GSTIN in front of them on every row. Neither
 * can be right for everybody, and both are one line of preference away.
 *
 * Four decisions.
 *
 * **A preference belongs to a PERSON, not to a shop.** The owner and the counter staff use the
 * same order list for different jobs, and one of them rearranging it for the other is a
 * worse outcome than neither being able to.
 *
 * **The registry lives in code.** A column is a field a component knows how to render, so a
 * stored key that no longer exists must be ignored rather than break the screen — which is what
 * `resolveColumns` does, silently, on every read.
 *
 * **Some columns cannot be hidden.** A customer row without a name is not a shorter row, it is
 * an unusable one. `alwaysVisible` is what says so, and it is enforced on the way out rather
 * than trusted to the picker.
 *
 * **Order is part of the preference.** Somebody who moves "last order" to the front did that
 * because it is what they scan for, and remembering only which columns they kept would throw
 * away the more useful half of what they said.
 */

/* -------------------------------------------------------------------------------------------
 * The tables
 * ---------------------------------------------------------------------------------------- */

/**
 * `customers` is NOT here any more. The customer book became a sheet whose columns are the
 * shop's field configuration (Settings → Fields), the same as the enquiry book — two answers to
 * "which columns does this list show" would disagree the first time either was changed. A
 * stored preference for it is harmless: it is never read, because only these keys are.
 */
export const TABLE_KEYS = ['orders', 'invoices', 'payments'] as const;
export type TableKey = (typeof TABLE_KEYS)[number];
export const tableKeySchema = z.enum(TABLE_KEYS);

export interface TableColumnDefinition {
  readonly key: string;
  /** Default English; the UI resolves `tables.<table>.<key>` through i18next. */
  readonly defaultLabel: string;
  /** Shown unless somebody turns it off. False for the ones most shops do not want. */
  readonly defaultVisible: boolean;
  /** Cannot be hidden. A row without this is not a shorter row, it is an unusable one. */
  readonly alwaysVisible?: true;
}

export const TABLE_COLUMNS: Readonly<Record<TableKey, readonly TableColumnDefinition[]>> =
  Object.freeze({
    orders: [
      { key: 'number', defaultLabel: 'Order number', defaultVisible: true, alwaysVisible: true },
      { key: 'customer', defaultLabel: 'Customer', defaultVisible: true },
      { key: 'stage', defaultLabel: 'Stage', defaultVisible: true },
      { key: 'promisedDate', defaultLabel: 'Promised', defaultVisible: true },
      { key: 'total', defaultLabel: 'Total', defaultVisible: true },
      { key: 'balance', defaultLabel: 'Balance', defaultVisible: true },
      { key: 'items', defaultLabel: 'Garments', defaultVisible: false },
    ],
    invoices: [
      { key: 'number', defaultLabel: 'Invoice number', defaultVisible: true, alwaysVisible: true },
      { key: 'customer', defaultLabel: 'Customer', defaultVisible: true },
      { key: 'issuedAt', defaultLabel: 'Issued', defaultVisible: true },
      { key: 'taxable', defaultLabel: 'Taxable', defaultVisible: false },
      { key: 'tax', defaultLabel: 'Tax', defaultVisible: false },
      { key: 'total', defaultLabel: 'Total', defaultVisible: true },
    ],
    payments: [
      { key: 'receivedAt', defaultLabel: 'Date', defaultVisible: true, alwaysVisible: true },
      { key: 'customer', defaultLabel: 'Customer', defaultVisible: true },
      { key: 'order', defaultLabel: 'Order', defaultVisible: true },
      { key: 'method', defaultLabel: 'Method', defaultVisible: true },
      { key: 'reference', defaultLabel: 'Reference', defaultVisible: false },
      { key: 'amount', defaultLabel: 'Amount', defaultVisible: true },
    ],
  });

export function tableColumns(table: TableKey): readonly TableColumnDefinition[] {
  return TABLE_COLUMNS[table];
}

/** The columns a table shows when nobody has said otherwise. */
export function defaultColumnKeys(table: TableKey): string[] {
  return tableColumns(table)
    .filter((column) => column.defaultVisible)
    .map((column) => column.key);
}

/* -------------------------------------------------------------------------------------------
 * The preference
 * ---------------------------------------------------------------------------------------- */

export const saveTablePreferenceSchema = z.object({
  table: tableKeySchema,
  /**
   * The visible columns, IN ORDER. An empty array is a valid answer — it means "everything but
   * the ones that cannot be hidden" — and is not the same as having no preference at all.
   */
  columns: z.array(z.string().max(40)).max(40),
});
export type SaveTablePreferenceInput = z.infer<typeof saveTablePreferenceSchema>;

export const tablePreferenceSchema = z.object({
  table: tableKeySchema,
  /** What is actually shown, resolved against the registry — never the raw stored list. */
  columns: z.array(z.string()),
  /** True when this person has said something. False means these are the defaults. */
  customised: z.boolean(),
});
export type TablePreference = z.infer<typeof tablePreferenceSchema>;

export const tablePreferencesSchema = z.object({
  tables: z.array(tablePreferenceSchema),
});
export type TablePreferences = z.infer<typeof tablePreferencesSchema>;

/**
 * What a table actually shows, given what somebody stored.
 *
 * THE REGISTRY WINS. A stored key no column defines any more is dropped silently — a screen
 * must not break because a field was renamed in a release — and every `alwaysVisible` column is
 * put back whatever the stored list says, at the position the registry gives it. That second
 * rule is enforced HERE rather than in the picker, because a picker is a UI and this is a
 * guarantee.
 */
export function resolveColumns(table: TableKey, stored: readonly string[] | null): string[] {
  const definitions = tableColumns(table);
  const known = new Set(definitions.map((column) => column.key));
  if (!stored) return defaultColumnKeys(table);

  const chosen = stored.filter((key) => known.has(key));
  const required = definitions.filter((column) => column.alwaysVisible === true);

  const out = [...chosen];
  for (const column of required) {
    if (!out.includes(column.key)) {
      // Put back at the position the registry gives it, so a name column somebody tried to
      // hide reappears at the front rather than tacked on the end.
      const index = definitions.findIndex((one) => one.key === column.key);
      out.splice(Math.min(index, out.length), 0, column.key);
    }
  }
  return out;
}

/** True when a column is showing. What a component asks, once per column, while rendering. */
export function showsColumn(columns: readonly string[], key: string): boolean {
  return columns.includes(key);
}
